import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://livelovespain.com/api';

//console.log('The API Base URL:', BASE_URL);

const api = axios.create({
    withCredentials: false,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    }
});

// Update request interceptor
api.interceptors.request.use(
    (config) => {
        //console.log(`Making ${config.method.toUpperCase()} request to: ${config.baseURL}${config.url}`);
        
        // Special handling for support contact endpoint
        //if (config.url === '/support/contact') {
            //console.log('Support contact request data:', {
            //    ...config.data,
            //    captchaToken: config.data.captchaToken ? '[REDACTED]' : undefined
            //});
        //}
        
        // Only add auth token if not already present
        if (!config.headers.Authorization) {
            const token = localStorage.getItem('authToken');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add response interceptor with limited retries
api.interceptors.response.use(
    response => response,
    async error => {
        const config = error.config;
        config.retryCount = config.retryCount || 0;
        
        // Check if this is an email service error
        const isEmailServiceError = error.response?.data?.message?.includes('Unable to process your request');
        
        // Only retry on network errors or specific status codes, excluding email service errors
        if (
            !isEmailServiceError && // Don't retry email service errors
            config.retryCount < 2 && 
            (error.message === 'Network Error' ||
            (error.response?.status >= 500 && error.response?.status < 600)) &&
            error.response?.status !== 429 // Don't retry on rate limit
        ) {
            config.retryCount += 1;
            
            // Get retry delay from response header or use exponential backoff
            const retryAfter = error.response?.headers['retry-after'] || 
                Math.min(1000 * (2 ** config.retryCount), 10000);
            
            await new Promise(resolve => setTimeout(resolve, retryAfter));
            
            return api.request(config);
        }

        return Promise.reject(error);
    }
);

// Update error logging
api.interceptors.response.use(
    response => response,
    error => {
        // Don't log 401 errors as they are expected for non-authenticated users
        if (error.response?.status !== 401) {
            console.error('API Error:', {
                url: error.config?.url,
                method: error.config?.method,
                status: error.response?.status,
                data: error.response?.data,
                message: error.message,
                retryCount: error.config?.retryCount || 0
            });
        }
        return Promise.reject(error);
    }
);

// Update auth token interceptor
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        // Add debug logging
        //console.log('Request config:', {
        //    url: config.url,
        //    method: config.method,
        //    hasToken: !!token,
        //    headers: {
        //        ...config.headers,
        //        Authorization: token ? 'Bearer [REDACTED]' : undefined
        //    }
        //});
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Add specific configuration for streaming endpoints
export const streamConfig = {
    timeout: 60000,
    responseType: 'stream',
    headers: {
        'Accept': 'text/plain',
        'Cache-Control': 'no-cache',
        'X-Requested-With': 'XMLHttpRequest'
    }
};

export const endpoints = {
    health: `${BASE_URL}/health`,
    auth: {
        health: `${BASE_URL}/auth/health`,
        login: `${BASE_URL}/auth/login`,
        register: `${BASE_URL}/auth/register`,
        checkEmail: `${BASE_URL}/auth/check-email`,
        verify: `${BASE_URL}/auth/verify`,
        verifyEmail: `${BASE_URL}/auth/verify`,
        resendVerification: `${BASE_URL}/auth/resend-verification`,
        setPassword: `${BASE_URL}/auth/set-password`,
        forgotPassword: `${BASE_URL}/auth/forgot-password`,
        resetPassword: `${BASE_URL}/auth/reset-password`,
        validateResetToken: `${BASE_URL}/auth/validate-reset-token`,
        passwordChangedNotification: `${BASE_URL}/auth/password-changed-notification`,
        google: `${BASE_URL}/auth/google`,
        googleCallback: `${BASE_URL}/auth/google/callback`,
    },
    tasks: {
        filtered: `${BASE_URL}/tasks/filtered`,
        details: (taskId) => `${BASE_URL}/tasks/${taskId}`,
        all: `${BASE_URL}/tasks`,
        feedback: `${BASE_URL}/tasks/:taskId/feedback`,
    },
    userSteps: {
        getByTaskId: (taskId, userId) => `${BASE_URL}/userSteps/${taskId}/${userId}`,
        updateStatus: `${BASE_URL}/userSteps/updateStatus`
    },
    userTasks: {
        getByUserId: (userId) => `${BASE_URL}/userTasks/user/${userId}`,
        updateStatus: `${BASE_URL}/userTasks/updateStatus`,
        getStatus: (taskId, userId) => `${BASE_URL}/userTasks/status/${taskId}/${userId}`
    },
    profile: {
        get: `${BASE_URL}/profile`,
        update: `${BASE_URL}/profile`
    },
    directory: {
        get: `${BASE_URL}/directory`,
        categories: `${BASE_URL}/directory/categories`,
    },
    userProfile: {
        getByUserId: (userId) => `${BASE_URL}/profile/user/${userId}`,
        update: `${BASE_URL}/profile/update`
    },
    spain: {
        insights: `${BASE_URL}/spaininsights`,
        getInsight: (id) => `${BASE_URL}/spaininsights/${id}`,
    },
    features: {
        get: `${BASE_URL}/features`,
        add: `${BASE_URL}/features/add`,
        upvote: (id) => `${BASE_URL}/features/${id}/upvote`,
    },
    userFeedback: {
        add: `${BASE_URL}/feedback/submit`,
        get: `${BASE_URL}/feedback/get`
    },
    admin: {
        status: `${BASE_URL}/admin/status`,
        base: `${BASE_URL}/admin`,
        users: `${BASE_URL}/admin/users`,
        tasks: `${BASE_URL}/admin/tasks`,
        directory: `${BASE_URL}/admin/directory`,
        insights: `${BASE_URL}/admin/spaininsights`,
        updatePermissions: (userId) => `${BASE_URL}/admin/users/${userId}/permissions`,
        checkStatus: `${BASE_URL}/admin/status`,
        subscriptionAnalytics: `${BASE_URL}/admin/analytics/subscriptions`,
        downloadSubscriberEmails: `${BASE_URL}/admin/download-subscriber-emails`,
        feedback: `${BASE_URL}/admin/feedback`,
        feedbackBulkRead: `${BASE_URL}/admin/feedback/bulk/read`,
        downloadFeedback: `${BASE_URL}/admin/feedback/download`,
        deleteTask: (taskId) => `${BASE_URL}/admin/tasks/${taskId}`,
        downloadTasks: `${BASE_URL}/admin/tasks/download`,
        importTasks: `${BASE_URL}/admin/tasks/import`,
        checkLinks: `${BASE_URL}/admin/check-links`,
        taskFeedback: `${BASE_URL}/admin/tasks/feedback`,
        taskFeedbackBulkRead: `${BASE_URL}/admin/tasks/feedback/bulk/read`,
        taskFeedbackStats: `${BASE_URL}/admin/tasks/feedback/stats`,
        taskFeedbackExport: `${BASE_URL}/admin/tasks/feedback/export`,
        updateTaskFeedback: (feedbackId) => `${BASE_URL}/admin/tasks/feedback/${feedbackId}`,
        features: `${BASE_URL}/admin/features`,
        updateFeatureStatus: (featureId) => `${BASE_URL}/admin/features/${featureId}/status`,
        costs: {
            cities: `${BASE_URL}/admin/costs/cities`,
            variables: `${BASE_URL}/admin/costs/variables`,
            deleteCity: (cityId) => `${BASE_URL}/admin/costs/cities/${cityId}`
        },
        trialReminders: {
            test: `${BASE_URL}/admin/test-trial-reminders`,
            config: `${BASE_URL}/admin/trial-reminder-config`
        },
        articles: {
            list: `${BASE_URL}/admin/articles`,
            create: `${BASE_URL}/admin/articles`,
            update: (articleId) => `${BASE_URL}/admin/articles/${articleId}`,
            delete: (articleId, categoryId) => `${BASE_URL}/admin/articles/${articleId}?categoryId=${encodeURIComponent(categoryId)}`,
            translate: (articleId) => `${BASE_URL}/admin/articles/${articleId}/translate`
        },
        media: {
            uploadFeatured: `${BASE_URL}/admin/articles/featured-image`
        },
        authors: {
            list: `${BASE_URL}/admin/authors`,
            create: `${BASE_URL}/admin/authors`,
            update: (id) => `${BASE_URL}/admin/authors/${id}`,
            delete: (id) => `${BASE_URL}/admin/authors/${id}`
        }
    },
    subscription: {
        status: `${BASE_URL}/subscription/status`,
        createCheckout: `${BASE_URL}/subscription/create-checkout`,
        success: `${BASE_URL}/subscription/success`,
        cancel: `${BASE_URL}/subscription/cancel`,
        toggleAutoRenewal: `${BASE_URL}/subscription/toggle-auto-renewal`,
        validateCoupon: `${BASE_URL}/subscription/validate-coupon`
    },
    support: {
        base: `${BASE_URL}/support`,
        categories: `${BASE_URL}/support/categories`,
        topics: `${BASE_URL}/support/topics`,
        search: `${BASE_URL}/support/search`,
        contact: `${BASE_URL}/support/contact`
    },
    costs: {
        calculatorData: `${BASE_URL}/costs/calculator-data`,
        city: (cityId) => `${BASE_URL}/costs/cities/${cityId}`,
        variables: `${BASE_URL}/costs/variables`,
        exportPDF: `${BASE_URL}/costs/export-pdf`
    },
    chat: {
        send: `${BASE_URL}/chat`,
        stream: `${BASE_URL}/chat/stream`
    }
};

export { api };
export default BASE_URL; 